import { mergeAttributes, Node } from '@tiptap/core';
import { SEGMENTS_COLOR_LADDER } from '@/app/constants';
import type { SegmentType } from '@/app/types';

export interface TimeTagOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    timeTag: {
      setTimeTag: (timeTag?: Partial<SegmentType>) => ReturnType;
    };
  }
}

export const TimeTag = Node.create<TimeTagOptions>({
  name: 'timeTag',
  group: 'inline',
  inline: true,
  selectable: true,
  draggable: false,
  atom: true,
  marks: '',
  content: '',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      id: { default: '' },
      start: { default: 0 },
      end: { default: 5 },
      color: {
        default: SEGMENTS_COLOR_LADDER[0],
        parseHTML: (element) => element.getAttribute('color') || SEGMENTS_COLOR_LADDER[0],
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-time-tag]',
      },
    ];
  },

  renderHTML({ node }) {
    const color = node.attrs.color || SEGMENTS_COLOR_LADDER[0];
    const rgbaColor = `${color}a3`;

    return [
      'span',
      mergeAttributes({
        ...this.options.HTMLAttributes,
        ...node.attrs,
        'data-time-tag': '',
        contenteditable: false,
        class:
          'inline-flex min-w-16 px-2 h-6 rounded-[100px] text-system-black text-sm font-normal items-center justify-center cursor-pointer text-white mr-2 select-none',
        style: `background: ${rgbaColor}`,
      }),
      `${node.attrs.start}s-${node.attrs.end}s`,
    ];
  },

  addCommands() {
    return {
      setTimeTag:
        (timeTag?: Partial<SegmentType>) =>
        ({ chain }) => {
          const { id, startTime, endTime, color } = timeTag ?? {};

          return chain()
            .insertContent({
              type: this.name,
              attrs: {
                id,
                start: startTime,
                end: endTime,
                color: color,
              },
            })
            .insertContent('')
            .run();
        },
    };
  },
});
